import { Pack, Representation, Wire, WireDef, Wiring } from "./Types";
import memoizeOne from "memoize-one";

export const TEMPLATE_REGEX = /{{\s?template:(\"[\w\s]+\"|\w+)\s?}}/g;
export const FIELD_REGEX = /{{\s*(.+?)\s*?}}/g;
export const FIELD_IF_REGEX = /{{#(.+?)}}/g;
export const FIELD_ENDIF_REGEX = /{{\/(.+?)}}/g;

const MAX_ITERS = 10;
const MAX_DEPTH = 4;

export const processFields = (
  htmlText: string,
  baseFields: { [key: string]: string },
  conceptFields: { [key: string]: string },
) => {
  return htmlText.replaceAll(
    FIELD_REGEX,
    (substring: string, ...args: any[]) => {
      let field = args[0] as string;
      if (field === "FrontSide") {
        field = "Front";
      }
      const value = baseFields?.[field] ?? conceptFields?.[field] ?? "";
      return value;
    },
  );
};
export const processFieldIfs = memoizeOne((
  htmlText: string,
  fields: { [key: string]: string },
): string => {
  return processFieldIfsRecursive(htmlText, fields);
});

const getWireDefs = (wireDefString: string): WireDef[] => {
  const biWire = wireDefString.split("<>");
  if (biWire.length === 2) {
    return [
      { from: biWire[0], to: biWire[1] },
      { from: biWire[1], to: biWire[0] },
    ];
  }
  const uniWire = wireDefString.split(">");
  if (uniWire.length === 2) {
    return [{
      from: uniWire[0],
      to: uniWire[1],
    }];
  }

  throw new Error(`Invalid wire definition: ${wireDefString}`);
};

const processFieldIfsRecursive = (
  htmlText: string,
  fields: { [key: string]: string },
  depth: number = 0,
) => {
  // console.log(concept);
  // console.log("parsing", htmlText);
  if (depth >= MAX_DEPTH) {
    return htmlText;
  }
  let newHtmlText = htmlText;
  for (let i = 0; i < MAX_ITERS; i++) {
    // Replace from first match of FIELD_IF_REGEX to FIELD_ENDIF_REGEX
    FIELD_IF_REGEX.lastIndex = 0;
    const fieldIf = FIELD_IF_REGEX.exec(newHtmlText);
    if (!fieldIf) {
      break;
    }
    // console.log('START', fieldIf[1])
    let foundEnd = false;
    FIELD_ENDIF_REGEX.lastIndex = 0;
    let fieldEndif: RegExpExecArray | null = null;
    for (let j = 0; j < MAX_ITERS; j++) {
      fieldEndif = FIELD_ENDIF_REGEX.exec(newHtmlText);
      if (!fieldEndif) {
        break;
      }
      if (fieldEndif[1] === fieldIf[1]) {
        foundEnd = true;
        break;
      }
    }
    if (!foundEnd) {
      break;
    }
    // console.log('END', fieldEndif![1])
    const replacedBody = newHtmlText.slice(
      fieldIf.index,
      fieldEndif!.index + fieldEndif![0].length,
    );
    const fieldName = fieldIf[1];
    const fieldValue = fields[fieldName] ?? "";
    if (!fieldValue) {
      // erase
      // console.log('ERASING', fieldIf[1])
      newHtmlText = newHtmlText.replace(replacedBody, "");
      continue;
    }
    // console.log('RECURSING INSIDE', fieldIf[1]);
    const innerBody = processFieldIfsRecursive(
      replacedBody.substring(
        fieldIf[0].length,
        replacedBody.length - fieldEndif![0].length,
      ),
      fields,
      depth + 1,
    );

    newHtmlText = newHtmlText.replace(
      replacedBody,
      innerBody,
    );
  }
  return newHtmlText;
};

export const getAllWires = (pack: Pack) => {
  const wires: Wire[] = [];
  const reps: Representation[] = [];

  let wireDefs: WireDef[] = [];
  for (let i = 0; i < pack.data.wiring.length; i++) {
    const wire = pack.data.wiring[i];
    wireDefs.push(...getWireDefs(wire));
  }
  for (let i = 0; i < pack.data.concepts.length; i++) {
    const concept = pack.data.concepts[i];
    for (let j = 0; j < wireDefs.length; j++) {
      const wireDef = wireDefs[j];
      const fromRep = concept.reps[wireDef.from];
      const toRep = concept.reps[wireDef.to];
      if (fromRep && toRep) {
        wires.push({
          concept: concept.id,
          from: wireDef.from,
          to: wireDef.to,
          tags: [`$${pack.metadata.id}`],
        });
      }
    }
  }
  return wires;
};

export const cardsCount = (pack: Pack) => {
  return getAllWires(pack).length;
};
